import {
  useSuspenseInfiniteQuery,
  useQueryClient,
  useMutation,
} from "@tanstack/react-query";
import {
  addVehicle,
  deleteVehicle,
  getVehicles,
  updateVehicle,
} from "../network/api/vehicles.api";
import { CarCreate, UpdateCar } from "../types";

export const useVehicles = (filtersObject: GetVehiclesPayload) =>
  useSuspenseInfiniteQuery({
    queryKey: ["vehicles", filtersObject],
    queryFn: ({ pageParam }) => {
      return getVehicles({
        page: pageParam,
        limit: filtersObject.limit ?? 5,
        make: filtersObject.make ?? undefined,
        search: filtersObject.search ?? undefined,
        minPrice: filtersObject.minPrice ?? undefined,
        maxPrice: filtersObject.maxPrice ?? undefined,
        minYear: filtersObject.minYear ?? undefined,
        maxYear: filtersObject.maxYear ?? undefined,
        fuelType: filtersObject.fuelType ?? undefined,
        transmission: filtersObject.transmission ?? undefined,
        condition: filtersObject.condition ?? undefined,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.flatMap((p) => p.vehicles).length;
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    meta: {
      errorMessage: "Failed to load vehicles. Please try again.",
    },
  });

export const useAddVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CarCreate) => addVehicle(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["vehicles"],
      });
    },
  });
};

export const useUpdateVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Partial<UpdateCar>) => updateVehicle(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["vehicles"],
      });
    },
  });
};

export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, images }: DeleteVehiclePayload) =>
      deleteVehicle(id, images),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["vehicles"],
      });
    },
  });
};
